import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, CheckCircle, AlertCircle, Loader2, ArrowRight } from 'lucide-react';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import AnimatedBackground from '../components/AnimatedBackground';
import api from '../services/api';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Minimum 6 caracteres'); return; }
    if (password !== confirm) { toast.error('Les mots de passe ne correspondent pas'); return; }
    setLoading(true);
    setError('');
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la reinitialisation');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AnimatedBackground>
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] p-8 max-w-md w-full text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-heading text-xl font-bold text-white mb-2">Lien invalide</h1>
            <p className="text-slate-400 text-sm mb-6">Ce lien de reinitialisation est invalide ou a expire.</p>
            <Link to="/login" className="text-brand font-heading font-bold text-xs uppercase tracking-wider hover:underline">
              Retour a la connexion
            </Link>
          </div>
        </div>
      </AnimatedBackground>
    );
  }

  return (
    <AnimatedBackground>
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="reset-password-page">
        <div className="w-full max-w-md">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
            <Link to="/"><img src="/logo-light.png" alt="iBookSport" className="h-12 mx-auto" /></Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] overflow-hidden">
            <div className="bg-brand px-8 py-5">
              <h1 className="font-heading text-xl font-black uppercase tracking-wider text-white">
                {done ? 'Mot de passe modifie' : 'Nouveau mot de passe'}
              </h1>
            </div>

            {done ? (
              <div className="p-8 text-center">
                <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <p className="text-white text-sm mb-6">Votre mot de passe a ete reinitialise avec succes.</p>
                <Link to="/login">
                  <button className="w-full h-12 bg-brand hover:bg-brand/90 text-white font-heading font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2" data-testid="reset-go-login">
                    Se connecter <ArrowRight className="w-4 h-4" />
                  </button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-8 space-y-5">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
                  </div>
                )}
                <div>
                  <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimum 6 caracteres"
                      className="w-full h-12 pl-11 pr-4 bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-slate-600 outline-none text-sm focus:border-brand/60 transition-all"
                      data-testid="reset-password-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1.5 block">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500 w-4 h-4" />
                    <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repetez le mot de passe"
                      className="w-full h-12 pl-11 pr-4 bg-white/[0.06] border border-white/[0.1] text-white placeholder:text-slate-600 outline-none text-sm focus:border-brand/60 transition-all"
                      data-testid="reset-confirm-input" />
                  </div>
                </div>
                <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                  className="w-full h-12 bg-brand hover:bg-brand/90 text-white font-heading font-bold text-xs uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Reinitialiser <ArrowRight className="w-4 h-4" /></>}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </AnimatedBackground>
  );
};

export default ResetPasswordPage;
